/**
 * История ранее открытых репозиториев в localStorage.
 *
 * Используется на HomePage для подсказок и one-click открытия. Пишется
 * двумя путями: при submit формы на HomePage и при успешной загрузке
 * projects на RepoPage (чтобы прямые URL-визиты тоже попадали в историю).
 */

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY  = 'pflow:repo-history';
const CHANGE_EVENT = 'pflow-repo-history-change';
const MAX_ITEMS    = 20;

export interface RepoHistoryEntry {
    owner:        string;
    repo:         string;
    lastOpenedAt: number;
}

function read(): RepoHistoryEntry[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter((e): e is RepoHistoryEntry =>
                typeof e === 'object' && e !== null
                && typeof (e as RepoHistoryEntry).owner === 'string'
                && typeof (e as RepoHistoryEntry).repo === 'string'
                && typeof (e as RepoHistoryEntry).lastOpenedAt === 'number')
            .sort((a, b) => b.lastOpenedAt - a.lastOpenedAt);
    } catch {
        return [];
    }
}

function write(items: RepoHistoryEntry[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
        window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
    } catch {
        // localStorage может быть недоступен (private mode / квота) — тихо игнорируем.
    }
}

export function useRepoHistory(): {
    history:    RepoHistoryEntry[];
    addRepo:    (owner: string, repo: string) => void;
    removeRepo: (owner: string, repo: string) => void;
} {
    const [history, setHistory] = useState<RepoHistoryEntry[]>(() => read());

    useEffect(() => {
        function refresh(): void { setHistory(read()); }
        window.addEventListener('storage', refresh);
        window.addEventListener(CHANGE_EVENT, refresh as EventListener);
        return () => {
            window.removeEventListener('storage', refresh);
            window.removeEventListener(CHANGE_EVENT, refresh as EventListener);
        };
    }, []);

    const addRepo = useCallback((owner: string, repo: string) => {
        const cur  = read();
        const rest = cur.filter(e => !(e.owner === owner && e.repo === repo));
        write([{ owner, repo, lastOpenedAt: Date.now() }, ...rest]);
    }, []);

    const removeRepo = useCallback((owner: string, repo: string) => {
        const cur = read();
        write(cur.filter(e => !(e.owner === owner && e.repo === repo)));
    }, []);

    return { history, addRepo, removeRepo };
}
